<script>
    import { redirect, page } from "@roxi/routify";
    import { createEventDispatcher, onMount } from "svelte";
    import ColorPicker from "svelte-awesome-color-picker";
    import PanelHeader from "../_components/PanelHeader.svelte";
    import PanelDeleteSection from "../_components/PanelDeleteSection.svelte";
    import ConfirmationButton from "../_components/ConfirmationButton.svelte";
    import CtaPlaceholder, {
        CtaPlaceholderType,
    } from "../_components/CtaPlaceholder.svelte";
    import InviteNewUsersPlaceholder from "$/components/InviteNewUsersPlaceholder.svelte";
    import TenantAuthority from "$/components/TenantAuthority.svelte";
    import AddTenantAuthority from "$/components/AddTenantAuthority.svelte";
    import { RecentTopicsStore } from "$/stores/RecentTopicsStore";
    import { ClusterStore } from "$/stores/ClusterStore";
    import { TenantStore } from "$/stores/TenantStore";
    import { TokenStore } from "$/stores/TokenStore";
    import { formattedDate } from "$/utils/date";
    import { ServiceConsts } from "$/services/ServiceConsts";
    import PanelLoadingAnimation from "../_components/PanelLoadingAnimation.svelte";
    import { ActiveTopicStore, ActiveTopic } from "$/stores/extension/ActiveTopicStore";
    import {
        getEffectivePricingPlan,
        debounce,
        getTextColor,
    } from "$/utils/Utils";

    let tenantAuthorities;
    let ownerAuthority;
    let isUserOwner;

    let hex;
    const updateThemeColor = debounce(async (hex) => {
        await TenantStore.edit({
            ...$TenantStore.active,
            theme: { ...$TenantStore.active.theme, primaryColor: hex },
        });
    }, 500);

    let logoUrl;
    let logoUploadLoading = false;
    let logoUploadTooBig = false;

    // logoUploadTooBig show for 2 seconds only
    $: if (logoUploadTooBig) {
        setTimeout(() => {
            logoUploadTooBig = false;
        }, 2000);
    }

    const dispatch = createEventDispatcher();

    $: {
        tenantAuthorities =
            $TenantStore !== null ? $TenantStore.activeAuthorities : null;
        ownerAuthority = tenantAuthorities?.find(
            (a) => a.authority === "ROLE_OWNER",
        );
        isUserOwner =
            ownerAuthority && ownerAuthority?.user.id === $TokenStore.user.id;
    }

    function handleTenantNameEdited(event) {
        let tenantNameEdited =
            event.target.value.trim() === ""
                ? "Untitled Workspace"
                : event.target.value.trim();
        TenantStore.edit({ ...$TenantStore.active, name: tenantNameEdited });
        event.target.value = tenantNameEdited;
    }

    async function deleteTenant() {
        await TenantStore.delete($TenantStore?.active.id);

        // load new tenant
        const personalTenant = $TenantStore.tenants.find(
            (tenant) => tenant.personal,
        );

        if (personalTenant !== undefined) {
            await TenantStore.setActive(personalTenant);
            await ClusterStore.init($TenantStore.active.id);
            await RecentTopicsStore.load();

            RecentTopicsStore.populateTopicsFromClusters(
                $ClusterStore.clusters,
            );
        }

        // redirect to home if on topic page
        if ($page.path === "/:topicId") $redirect(`/`);

        dispatch("close");
    }

    async function deleteAuthority(authorityId) {
        let authority = $TenantStore.activeAuthorities.find(
            (authority) => authority.id === authorityId,
        );
        await TenantStore.deleteAuthority(authorityId);

        if ($ActiveTopic && authority.user) {
            removeUserRoleFromActiveTopic(authority.user);
        }
    }

    function removeUserRoleFromActiveTopic(user) {
        let clusterRoles = ClusterStore.getTopicRoles(
            $ActiveTopic.id,
            $TokenStore.user.id,
            true,
        );
        let userRoles = $ActiveTopic.roles.filter((r) => r.role !== 10);
        let userExistsInClusterOrTopic = [...clusterRoles, ...userRoles].some(
            (role) => role.user.id === user.id,
        );
        if (!userExistsInClusterOrTopic) {
            ActiveTopicStore.removeCollaborator(user);
        }
    }

    function handleUpgrade() {
        $redirect("/payment");
        dispatch("close");
    }

    async function handleSubscriptionCancel() {
        await TenantStore.cancelSubscription($TenantStore?.active.id);
    }

    async function handleLogoUpload(event) {
        logoUploadLoading = true;
        // TODO: get max logo size from backend and edit this
        // for now max size is 2mb
        if (event.target.files[0].size > 2000000) {
            logoUploadTooBig = true;
            logoUploadLoading = false;
            return;
        }
        const file = event.target.files[0];
        try {
            await TenantStore.updateLogo(file);
            // set logoUrl to be able to show the new logo instantly
            logoUrl = URL.createObjectURL(file);
        } catch (e) {
            logoUploadTooBig = true;
        }

        logoUploadLoading = false;
    }

    $: subscription = {
        personal: $TenantStore?.active?.personal === 0,
        lifetime: $TenantStore?.active?.lifetime === true,
        effectivePricingPlan: getEffectivePricingPlan($TenantStore?.active),
        pricingPlan: $TenantStore?.active?.pricingPlan,
        expirationDate: $TenantStore?.active?.expirationDate,
        maxMembersCount: $TenantStore?.active?.subscription?.maxMembersCount,
        status: $TenantStore?.active?.subscription?.status,
    };

    onMount(() => {
        hex = $TenantStore?.active?.theme?.primaryColor || "#374151";
    });
</script>

<div class="workspace-settings">
    <PanelHeader
        title="Workspace Settings"
        on:close={() => dispatch("close")}
    />

    {#if !$TenantStore?.active}
        <PanelLoadingAnimation />
    {:else}
        <div class="workspace-settings-content">
            <div class="tenant">
                <h2 class="tenant-header">Workspace name</h2>

                <div class="tenant-name">
                    <div class="tenant-name-content">
                        <input
                            class="tenant-name__input"
                            value={$TenantStore?.active?.name}
                            on:change={handleTenantNameEdited}
                            disabled={!isUserOwner ||
                                subscription.effectivePricingPlan === -1}
                        />
                    </div>
                </div>
            </div>

            <div class="tenant">
                <h2 class="tenant-header">Shared Topic Styling</h2>

                {#if subscription.effectivePricingPlan === 0}
                    <CtaPlaceholder type={CtaPlaceholderType.COLOR_PICKER} />
                    <CtaPlaceholder
                        type={CtaPlaceholderType.LOGO_UPLOAD}
                        planName="Pro"
                        onClick={handleUpgrade}
                    />
                {:else if subscription.effectivePricingPlan === -1}
                    <CtaPlaceholder type={CtaPlaceholderType.COLOR_PICKER} />
                    <CtaPlaceholder
                        type={CtaPlaceholderType.LOGO_UPLOAD}
                        planName="Team"
                        onClick={handleUpgrade}
                    />
                {:else}
                    <ColorPicker 
                        bind:hex 
                        label="Choose theme color" 
                        isAlpha={false}
                        on:input={(e) => {
                            updateThemeColor(e.detail.hex);
                        }}
                    />
                    <div
                        class="tenant-logo"
                        style="background: {hex} !important"
                    >
                        {#if !logoUploadLoading && !logoUploadTooBig}
                            {#if !logoUrl}
                                <svg
                                    class="tenant-logo__upload"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke-width="1.5"
                                    stroke="currentColor"
                                    style="color: {getTextColor(
                                        hex,
                                    )} !important"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m6.75 12l-3-3m0 0l-3 3m3-3v6m-1.5-15H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                                    />
                                </svg>
                            {:else}
                                <img src={logoUrl} alt="logo" />
                            {/if}
                            <label
                                class:text-right={logoUrl}
                                for="logoUpload"
                                style="color: {getTextColor(hex)} !important"
                            >
                                Upload your logo
                            </label>
                            <input
                                on:change={handleLogoUpload}
                                id="logoUpload"
                                style="display: none;"
                                type="file"
                                accept="image/*"
                            />
                        {:else if logoUploadLoading && !logoUploadTooBig}
                            <div class="tenant-logo__loading rotate-endlessly">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke-width="1.5"
                                    stroke="currentColor"
                                    class="h-6 w-6"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                                    />
                                </svg>
                            </div>
                        {:else if logoUploadTooBig}
                            <p class="text-sm text-pink-500">
                                Logo is too big. Max size is 2MB.
                            </p>
                        {/if}
                    </div>
                {/if}
            </div>

            <div class="users">
                <h2 class="authority-header">
                    Members ({$TenantStore
                        ? TenantStore.getActiveMembersCount()
                        : 0})
                </h2>

                <!-- can view and handle members if active tenant is on TEAM licence -->
                {#if subscription.effectivePricingPlan === 2}
                    <div class="users-list">
                        {#if tenantAuthorities}
                            <TenantAuthority
                                authority={ownerAuthority}
                                editable={false}
                                showRole={true}
                            />

                            {#each tenantAuthorities.filter((a) => a.authority === "ROLE_USER" && a.user.username !== ownerAuthority?.user.username) as tenantAuthority (tenantAuthority.id)}
                                <TenantAuthority
                                    authority={tenantAuthority}
                                    editable={isUserOwner}
                                    showRole={true}
                                    on:delete={() =>
                                        deleteAuthority(tenantAuthority.id)}
                                />
                            {/each}

                            <AddTenantAuthority
                                tenantOwner={ownerAuthority.user}
                            />
                        {/if}
                    </div>
                {:else}
                    <div class="users-list">
                        <TenantAuthority
                            authority={ownerAuthority}
                            editable={false}
                            showRole={true}
                        />
                        {#if tenantAuthorities}
                            {#each tenantAuthorities.filter((a) => a.authority === "ROLE_USER" && a.user.username !== ownerAuthority?.user.username) as tenantAuthority (tenantAuthority.id)}
                                <TenantAuthority
                                    authority={tenantAuthority}
                                    editable={false}
                                />
                            {/each}
                        {/if}
                        <InviteNewUsersPlaceholder
                            ctaClick={handleUpgrade}
                            planName="Team"
                        />
                    </div>
                {/if}
            </div>

            <div class="users">
                <h2 class="authority-header">
                    Collaborators ({$TenantStore.collaborators.length})
                </h2>
                {#if $TenantStore.collaborators.length > 0}
                    <div class="flex flex-col gap-4">
                        {#each $TenantStore.collaborators as collaborator}
                            <TenantAuthority
                                authority={{
                                    authority: "ROLE_USER",
                                    user: collaborator,
                                }}
                                editable={false}
                                showRole={false}
                            />
                        {/each}
                    </div>
                {:else}
                    <span class="text-gray-300">No collaborators yet</span>
                {/if}
            </div>

            {#key $TenantStore?.active?.subscription}
                <div class="pricing-plan">
                    <h2 class="pricing-plan-header">Subscription</h2>
                    <div class="pricing-plan-card">
                        <svg
                            class="pricing-plan-card__icon"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"
                            />
                        </svg>
                        <div class="pricing-plan-card__text">
                            {#if subscription.effectivePricingPlan === 0}
                                Free
                            {:else if subscription.effectivePricingPlan === 1}
                                Personal Pro
                            {:else if subscription.pricingPlan === 2}
                                Team
                            {/if}
                        </div>
                        <div class="pricing-plan-card__expiration">
                            {#if subscription.effectivePricingPlan === 0}
                                Free Forever
                            {:else if subscription.lifetime}
                                <span
                                    class="pricing-plan-card__expiration__lifetime"
                                >
                                    Lifetime
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke-width="1.5"
                                        stroke="currentColor"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            d="M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z"
                                        />
                                    </svg>
                                </span>
                            {:else if TenantStore.isExpired()}
                                <span
                                    class="pricing-plan-card__expiration__lifetime"
                                >
                                    Expired
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke-width="1.5"
                                        stroke="currentColor"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            d="M15.182 16.318A4.486 4.486 0 0 0 12.016 15a4.486 4.486 0 0 0-3.198 1.318M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0ZM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Z"
                                        />
                                    </svg>
                                </span>
                            {:else if subscription.effectivePricingPlan > 0}
                                {TenantStore.isExpiring()
                                    ? "Expires"
                                    : "Renews"}
                                {formattedDate(
                                    $TenantStore?.active?.expirationDate,
                                )}
                            {/if}
                        </div>
                    </div>

                    {#if TenantStore.isManagedSubscription()}
                        <a
                            href={`${ServiceConsts.SERVER_URL}/billing/${$TenantStore?.active?.id}/portal`}
                            class="pricing-plan-button"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Billing & Invoices
                        </a>
                        <a
                            href={`${ServiceConsts.SERVER_URL}/billing/${$TenantStore?.active?.id}/payment`}
                            class="pricing-plan-button"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Update Payment Method
                        </a>

                        {#if !TenantStore.isCancelled() && !TenantStore.isExpired()}
                            <ConfirmationButton
                                handleAction={handleSubscriptionCancel}
                            >
                                Cancel Subscription
                            </ConfirmationButton>
                        {/if}
                    {/if}
                </div>
            {/key}

            {#if isUserOwner && $TenantStore?.active?.personal === false && subscription.effectivePricingPlan !== -1}
                <PanelDeleteSection
                    deletionConfirmed={deleteTenant}
                    description={"Deleting a workspace is permanent and cannot be undone."}
                    warningText={"Are you <b>absolutely sure</b> you want to delete this workspace?"}
                />
            {/if}
        </div>
    {/if}
</div>

<style>
    @import "../../../main.css";

    .workspace-settings {
        @apply flex h-full w-full flex-col overflow-auto bg-gray-100 !important;
    }

    .workspace-settings-content {
        @apply reset flex flex-1 flex-col gap-8 overflow-auto px-4 py-4 !important;
    }

    .tenant {
        @apply flex flex-col !important;
    }

    .tenant-header {
        @apply reset mb-2 !important;
    }

    .tenant-name {
        @apply flex flex-col gap-1 !important;
    }

    .tenant-name__input {
        @apply w-full text-sm outline-none !important;
    }

    .tenant-name__input:disabled {
        @apply bg-inherit;
    }

    .tenant-name-content {
        @apply flex flex-row items-center gap-1 rounded-full border border-gray-200 bg-white p-4 !important;
    }

    .tenant-logo {
        @apply mt-1 flex max-h-[53px] flex-row items-center justify-center gap-2 rounded-full border border-gray-200 bg-white p-4 !important;
    }

    .tenant-logo label {
        @apply h-full w-full cursor-pointer text-sm !important;
    }

    .tenant-logo img {
        @apply h-full rounded-md !important;
    }

    .tenant-logo__upload {
        @apply ml-2 h-6 w-6 !important;
    }

    .tenant-logo__loading {
        @apply flex cursor-default select-none items-center justify-center !important;
    }

    .users {
        @apply flex flex-col !important;
    }

    .users-list {
        @apply flex flex-col gap-4 !important;
    }

    .authority-header {
        @apply reset mb-2 !important;
    }

    .pricing-plan {
        @apply flex flex-col gap-2 !important;
    }

    .pricing-plan-header {
        @apply reset mb-2 !important;
    }

    .pricing-plan-card {
        @apply flex flex-row items-center gap-2 rounded-full border border-solid border-gray-200 bg-white px-4 py-2 !important;
    }

    .pricing-plan-card__icon {
        @apply h-5 w-5 !important;
    }

    .pricing-plan-card__text {
        @apply text-base !important;
    }

    .pricing-plan-card__expiration {
        @apply ml-auto text-xs !important;
    }

    .pricing-plan-card__expiration__lifetime {
        @apply flex flex-row items-center gap-1 !important;
    }

    .pricing-plan-card__expiration__lifetime svg {
        @apply h-4 w-4 !important;
    }

    .pricing-plan-button {
        @apply rounded-full bg-gray-400 px-4 py-2 text-center text-base text-white hover:bg-gray-500 !important;
    }

    .delete-header__flames svg {
        @apply h-4 w-4 !important;
    }

    .rotate-endlessly {
        animation: rotate 2s infinite linear;
    }

    @keyframes rotate {
        0% {
            transform: rotate(0);
        }

        100% {
            transform: rotate(360deg);
        }
    }
</style>
